<template>
  <v-dialog v-model="dialog" scrollable persistent max-width="1400px">
    <v-card>
      <v-card-title>
        <v-layout wrap>
          <v-flex xs12>
            <h3 style="margin-bottom: 30px; text-align: left;">{{$t('hotel.manageRateMatrix')}}</h3>
          </v-flex>
        </v-layout>
      </v-card-title>
      <v-card-text>
        <v-layout wrap>
          <v-flex xs12 sm12 md5>
            <v-layout wrap>
              <v-flex xs12>
                <v-data-table
                  :headers="rates.headers"
                  :items="room.rates">
                  <template v-slot:item="props">
                    <tr>
                      <td class="text-sm-left">
                        <v-edit-dialog
                          :return-value.sync="props.item.name"
                          lazy>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <div v-on="on">{{ props.item.name }}</div>
                            </template>
                            <span>Bearbeiten</span>
                          </v-tooltip>
                          <v-text-field
                            slot="input"
                            v-model="props.item.name"
                            :rules="rules"
                            label="Name"
                            single-line
                            required
                          ></v-text-field>
                        </v-edit-dialog>
                      </td>
                      <td class="text-sm-left">
                        <v-edit-dialog
                          :return-value.sync="props.item.percent"
                          lazy>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <div v-on="on">{{ props.item.percent }} %</div>
                            </template>
                            <span>Bearbeiten</span>
                          </v-tooltip>
                          <v-text-field
                            slot="input"
                            v-model="props.item.percent"
                            :rules="rules"
                            label="Prozent"
                            single-line
                            required
                          ></v-text-field>
                        </v-edit-dialog>
                      </td>
                      <td>
                        <v-layout wrap justify-end>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on }">
                              <v-icon medium @click="removeRate(props.item)" v-on="on" color="red">delete</v-icon>
                            </template>
                            <span>{{ $t("delete") }}</span>
                          </v-tooltip>
                        </v-layout>
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-flex>
              <v-flex xs12>
                <v-form v-model="rates.form" ref="addRateFrom" lazy-validation style="width: 100%;">
                  <v-layout wrap>
                    <v-flex xs12 sm6 md4>
                      <v-text-field
                        color="primary"
                        :label="$t('event.name')"
                        :rules="rules"
                        v-model="rate.name"
                        required></v-text-field>
                    </v-flex>
                    <v-flex xs12 sm6 md3 offset-md1>
                      <v-text-field
                        color="primary"
                        :label="$t('season.percentage')"
                        :rules="rules"
                        v-model="rate.percent"
                        required></v-text-field>
                    </v-flex>
                    <v-flex xs12 sm12 md3 offset-md1 pt-2 class="text-sm-right">
                      <v-btn color="primary" outlined @click="addRate()">
                        <v-icon>add</v-icon>
                      </v-btn>
                    </v-flex>
                  </v-layout>
                </v-form>
              </v-flex>
              <v-flex xs12 mt-2 v-if="other.rates.length > 0">
                <h2 style="margin-bottom: 20px">Rate von andere Zimmer übernehmen</h2>
              </v-flex>
              <v-flex xs6 v-if="other.rates.length > 0">
                <v-select
                  v-model="other.room"
                  :items="other.rates"
                  label="Zimmer auswählen"
                ></v-select>
              </v-flex>
              <v-flex xs6 class="text-sm-right" v-if="other.rates.length > 0">
                <v-btn outlined color="primary" style="margin-top: 10px" @click="takeRatesFromOtherRoom()">
                  Übernehmen
                </v-btn>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex xs12 sm12 md6 offset-md1 v-if="room.rates.length === 0">
            <v-alert :value="true" class="text-sm-center" color="error" icon="warning" outlined>
              {{ $t("hotel.rates.emptyList") }}
            </v-alert>
          </v-flex>
          <v-flex xs12 sm12 md6 offset-md1 v-else>
            <v-layout wrap row>
              <v-flex xs12 sm4>
                <v-select v-model="selectedRate"
                          :items="room.rates"
                          item-text="name"
                          item-value="percent"
                          label="Ausgewählte Rate"
                          single-line
                          hint="Rate auswählen und Matrix bestimmen.">
                </v-select>
              </v-flex>
              <v-flex xs12 sm7 offset-sm1 class="text-sm-left">
                <h2>Instruktionen</h2>
                <ul>
                  <li>Wählen Sie eine Rate aus.</li>
                  <li>Klicken Sie auf ein Feld um die Rate in diesen Bereich zu ändern.</li>
                  <li>Um die ganze Zeile zu ändern klicken Sie auf das Auslastungsfeld.</li>
                </ul>
              </v-flex>
              <v-flex xs12 mt-3>
                <h2 style="margin-bottom: 10px; text-align: left;">Raten-Matrix</h2>
                <table class="rate-matrix-table" border="0" cellspacing="0" cellpadding="0">
                  <tbody>
                    <tr v-for="occupancyRate in occupancyRates" :key="'occupancy'+occupancyRate">
                      <td>
                        <v-btn depressed small color="white" @click="addToMatrixWithOccupancyRate(occupancyRate)">
                          <h3>{{ occupancyRate }}</h3>
                        </v-btn>
                      </td>
                      <td v-for="(rate, index) in getRatesFor(occupancyRate)" :key="index">
                        <v-btn depressed small @click="addRateToMatrix(rate)" :class="[setColor(rate)]">
                          <span v-if="rate.percent === undefined">
                            N/A
                          </span>
                          <span v-else>
                            {{ rate.percent }}%
                          </span>
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot v-if="isCityHotel()">
                    <tr>
                      <td></td>
                      <td v-for="rangeSpan in cityRangeSpan" :key="rangeSpan">
                        <h3>{{rangeSpan}}</h3>
                      </td>
                    </tr>
                  </tfoot>
                  <tfoot v-else>
                    <tr>
                      <td></td>
                      <td v-for="rangeSpan in holidayRangeSpan" :key="rangeSpan">
                        <h3>{{rangeSpan}}</h3>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="secondary" text @click.native="dialog = false">{{ $t('close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import RateMatrixUtils from '../../../mixins/RateMatrixUtils';

  export default {
    name: 'ManageRateMatrix',
    mixins: [RateMatrixUtils],
    data() {
      return {
        dialog: false,
        rates: {
          headers: [
            { text: this.$i18n.t('event.name'), sortable: false, align: 'left', value: 'name' },
            { text: this.$i18n.t('season.rates'), align: 'left', value: 'percent' },
            { text: this.$i18n.t('manageYieldStaff.actions'), sortable: false, align: 'center' },
          ],
          form: undefined,
        },
        rate: {
          name: undefined,
          percent: undefined
        },
        rules: [
          v => !!v || this.$i18n.t('form.required'),
        ],
        selectedRate: undefined,
        hotelDTO: undefined,
        matrix: [
          { label: '' }
        ],
        room: {
          rates: []
        },
        occupancyRates: [100, 90, 80, 70, 60, 50, 40, 30, 20, 10, 0],
        cityRangeSpan: ['0', '1', '2', '3', '4', '5', '30', '60', '90', '180'],
        holidayRangeSpan: ['0', '1', '2', '3', '4', '30', '60', '90', '180'],
        other: {
          rates: [],
          room: undefined
        }
      };
    },
    mounted() {
      this.globalEvents.$on('openManageRateMatrix', (room, hotelDTO) => {
        this.hotelDTO = hotelDTO;
        this.other.rates = [];
        this.other.room = undefined;
        if (this.hotelDTO !== undefined) {
          for(let i = 0; i < hotelDTO.kognitivHotelRooms.length; i += 1) {
            const roomTemp = hotelDTO.kognitivHotelRooms[i];
            if (room.externalId === roomTemp.externalId) {
              this.room = roomTemp;
              if (this.room.rateMatrix === undefined) {
                this.room.rateMatrix = this.initRateMatrix(this.room.rateMatrix);
              }
            } else if (roomTemp.rateMatrix !== undefined && roomTemp.rates.length > 0) {
              this.other.rates.push(roomTemp.externalId);
            }
          }
          this.dialog = true;
        }
      });
    },
    methods: {
      removeRate(rate) {
        let index = -1;
        for (let i = 0; i < this.room.rates.length; i += 1) {
          if (this.room.rates[i].name === rate.name) {
            index = i;
            break;
          }
        }
        if (index !== -1) {
          this.room.rates.splice(index, 1);
        }
        for(let i = 0; i < this.room.rateMatrix.length; i += 1) {
          const matrix = this.room.rateMatrix[i];
          if (rate.percent === matrix.percent) {
            matrix.percent = undefined;
          }
        }
      },
      addRate() {
        if (this.$refs.addRateFrom.validate()) {
          const rate = {
            name: this.rate.name,
            percent: parseInt(this.rate.percent)
          };
          this.room.rates.push(rate);
          this.rate = {
            name: undefined,
            percent: undefined
          };
          this.$refs.addRateFrom.reset();
          this.room.rates.sort(function(a, b){return a.percent - b.percent});
        }
      },
      initRateMatrix(rateMatrix) {
        rateMatrix = [];
        if (this.isCityHotel()) {
          this.initCityHotel(rateMatrix);
        } else {
          this.initHolidayHotel(rateMatrix);
        }
        return rateMatrix;
      },
      addRateToMatrix(rateMod) {
        if (this.selectedRate !== undefined) {
          for (let i = 0; i < this.room.rateMatrix.length; i += 1) {
            const rate = this.room.rateMatrix[i];
            if (rate.occupancyRate === rateMod.occupancyRate
              && rate.from === rateMod.from) {
              rate.percent = this.selectedRate;
              break;
            }
          }
        }
      },
      addToMatrixWithOccupancyRate(occupancyRate) {
        for (let i = 0; i < this.room.rateMatrix.length; i += 1) {
          const rate = this.room.rateMatrix[i];
          if (rate.occupancyRate === occupancyRate) {
            rate.percent = this.selectedRate;
          }
        }
      },
      getRatesFor(occupancyRate) {
        const rates = [];

        for (let i = 0; i < this.room.rateMatrix.length; i += 1) {
          const rate = this.room.rateMatrix[i];
          if (rate.occupancyRate === occupancyRate) {
            rates.push(rate);
          }
        }

        return rates;
      },
      isCityHotel() {
        return this.hotelDTO.hotelType === 'CITYHOTEL';
      },
      setColor(rate) {
        if (rate.percent !== undefined) {
          if (rate.percent === this.room.rates[0].percent) {
            return 'lowset';
          } else if (rate.percent
            === this.room.rates[this.room.rates.length - 1].percent) {
            return 'highest';
          } else {
            for (let i = 1; i < this.room.rates.length - 1; i += 1) {
              const tempRate = this.room.rates[i];
              if (tempRate.percent === rate.percent) {
                if (i === 1) {
                  return 'random1';
                } else if (i === 2) {
                  return 'random2';
                } else if (i === 3) {
                  return 'random3';
                } else if (i === 4) {
                  return 'random4';
                } else if (i === 5) {
                  return 'random5';
                } else if (i === 6) {
                  return 'random6';
                } else {
                  return 'random';
                }
              }
            }
          }
        }
        return 'default';
      },
      takeRatesFromOtherRoom() {
        for(let i = 0; i < this.hotelDTO.kognitivHotelRooms.length; i += 1) {
          const roomTemp = this.hotelDTO.kognitivHotelRooms[i];
          if (this.other.room === roomTemp.externalId) {
            this.room.rates = JSON.parse(JSON.stringify(roomTemp.rates));
            this.room.rateMatrix = JSON.parse(JSON.stringify(roomTemp.rateMatrix));
            // RESET ID IF IT HAS ONE
            for (let i = 0; i < this.room.rates.length; i += 1) {
              const rate = this.room.rates[i];
              if (rate.id !== undefined) {
                rate.id = null;
              }
            }
          }
        }
      }
    },
  };
</script>

<style lang="scss">
  .rate-matrix-table {
    width: 100%;

    .v-btn {
      width: 100%;
      min-width: auto;
      margin: 0;
      padding: 0;
      font-weight: 500;
      border-radius: 0;

      &.highest {
        background-color: #FF5252 !important;
      }
      &.lowset {
        background-color: #3f89ff !important;
      }
      &.default {
        background-color: #e0f2f1 !important;
      }
      &.random {
        background-color: transparent !important;
      }
      &.random1 {
        background-color: #4EC6FF !important;
      }
      &.random2 {
        background-color: #74ffd8 !important;
      }
      &.random3 {
        background-color: #FFEB73 !important;
      }
      &.random4 {
        background-color: #ffbd47 !important;
      }
      &.random5 {
        background-color: #01ff09 !important;
      }
      &.random6 {
        background-color: #d1abff !important;
      }
    }
  }
</style>